/**
 * Acknowledged, cancellable request/response over window events.
 *
 * - Every request carries a session scope and an AbortSignal. A timeout or a
 *   superseding transition aborts the signal, so the handler stops before its
 *   next write instead of continuing in the background.
 * - Handlers must return an explicit acknowledgement; `void` is a refusal.
 * - Acks that arrive after the request settled (timeout/abort) are ignored.
 * - `drainDemo()` resolves once every in-flight handler has settled, so exit
 *   and a new start never overlap an old reset or write.
 */
export interface DemoAck { ok: boolean; reason?: string }

const PREFIX = 'demo-req:';

export interface DemoRequestContext {
  signal: AbortSignal;
  sessionId: string | null;
  /** True while the request is still wanted (not timed out/aborted). */
  isLive: () => boolean;
}

interface RequestDetail<T> {
  payload: T;
  claimed: boolean;
  ctx: DemoRequestContext;
  respond: (ack: DemoAck) => void;
}

export interface RequestOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
  sessionId?: string | null;
}

const inFlight = new Set<Promise<unknown>>();

export function requestDemo<T, A extends DemoAck = DemoAck>(
  type: string, payload: T, opts: RequestOptions | number = {},
): Promise<A | DemoAck> {
  const o: RequestOptions = typeof opts === 'number' ? { timeoutMs: opts } : opts;
  const timeoutMs = o.timeoutMs ?? 8000;
  const local = new AbortController();
  const outer = o.signal;
  return new Promise((resolve) => {
    let settled = false;
    const done = (ack: DemoAck) => {
      if (settled) return; // late/stale acks are ignored
      settled = true;
      clearTimeout(timer);
      outer?.removeEventListener('abort', onOuterAbort);
      if (!ack.ok) local.abort();
      resolve(ack);
    };
    const onOuterAbort = () => done({ ok: false, reason: 'superseded' });
    if (outer?.aborted) { resolve({ ok: false, reason: 'superseded' }); return; }
    outer?.addEventListener('abort', onOuterAbort);
    const timer = setTimeout(() => done({ ok: false, reason: `No response for ${type}` }), timeoutMs);
    const ctx: DemoRequestContext = {
      signal: local.signal,
      sessionId: o.sessionId ?? null,
      isLive: () => !settled && !local.signal.aborted,
    };
    const detail: RequestDetail<T> = { payload, claimed: false, ctx, respond: done };
    window.dispatchEvent(new CustomEvent(PREFIX + type, { detail }));
    if (!detail.claimed) done({ ok: false, reason: `Nothing is ready to handle ${type}` });
  });
}

export function handleDemo<T>(
  type: string,
  fn: (payload: T, ctx: DemoRequestContext) => Promise<DemoAck | void> | DemoAck | void,
): () => void {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent<RequestDetail<T>>).detail;
    if (!detail || detail.claimed) return;
    detail.claimed = true;
    const run = Promise.resolve()
      .then(() => (detail.ctx.isLive() ? fn(detail.payload, detail.ctx) : { ok: false, reason: 'superseded' }))
      .then((ack) => detail.respond((ack as DemoAck | undefined) ?? { ok: false, reason: `${type}: no acknowledgement` }))
      .catch((err) => detail.respond({ ok: false, reason: (err as Error)?.message || 'Handler failed' }));
    inFlight.add(run);
    void run.finally(() => inFlight.delete(run));
  };
  window.addEventListener(PREFIX + type, listener);
  return () => window.removeEventListener(PREFIX + type, listener);
}

/** Resolve once every in-flight handler has settled. */
export async function drainDemo(): Promise<void> {
  while (inFlight.size) await Promise.allSettled([...inFlight]);
}

/** Resolve after the browser has painted twice. */
export const nextPaint = () =>
  new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

/** Wait for a DOM condition; false on timeout or abort. */
export async function waitFor(check: () => boolean, signal: AbortSignal, timeoutMs = 4000): Promise<boolean> {
  const until = Date.now() + timeoutMs;
  while (Date.now() < until) {
    if (signal.aborted) return false;
    if (check()) return true;
    await new Promise(r => setTimeout(r, 50));
  }
  return check();
}
